/*
  Terminal Reconciliation Synthetic Nodes and Edges

  This module generates synthetic (display-only) nodes and edges for non-legacy
  terminal reconciliation workflow nodes. These synthetic elements show the possible
  outcomes from the terminal recon workflow but are not persisted to the API.
*/

import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'react-flow-renderer';
import type {
  JourneyDocument,
  ReconciliationWorkflowNode,
  JourneyWorkflowMetadata,
  WorkflowOutcome,
} from '../types';
import { DEFAULT_EDGE_STYLE } from './reactFlow/constants';

/**
 * Check if a reconciliation workflow node is a non-legacy terminal recon node
 *
 * Non-legacy terminal recon nodes have:
 * - terminal: true
 * - legacy: false (or not set)
 *
 * @param node - The reconciliation workflow node to check
 * @returns True if this is a non-legacy terminal recon node
 */
function isNonLegacyTerminalReconNode(node: ReconciliationWorkflowNode): boolean {
  const { config } = node;
  return config.terminal === true && config.legacy !== true;
}

/**
 * Generate synthetic outcome nodes for a terminal recon workflow
 *
 * Creates outcome nodes for each outcome in the terminal recon workflow.
 * These nodes are marked as synthetic and will not be saved to the API.
 *
 * @param reconNodeId - The ID of the terminal recon workflow node
 * @param workflowOutcomes - Array of outcomes from the workflow
 * @returns Array of synthetic React Flow outcome nodes
 */
function generateSyntheticOutcomeNodes(
  reconNodeId: string,
  workflowOutcomes: WorkflowOutcome[],
): ReactFlowNode[] {
  return workflowOutcomes.map((outcome, index) => ({
    id: `synthetic-outcome-${reconNodeId}-${index}`,
    type: 'outcome',
    position: { x: 0, y: 0 }, // Will be set by layout engine
    data: {
      id: `synthetic-outcome-${reconNodeId}-${index}`,
      type: 'outcome',
      name: outcome.outcome,
      config: {
        outcome: outcome.outcome,
        customer_outcome_id: outcome.customer_outcome_id ?? null,
        terminal: true,
        reconciliation: true,
        outcome_icon: null,
        outcome_color: null,
      },
      label: outcome.outcome,
      tooltip: outcome.outcome,
      // Mark this node as synthetic so it can be identified
      isSyntheticTerminalReconOutcome: true,
      parentReconNodeId: reconNodeId, // For layout positioning
      outcomeIndex: index, // Position index for vertical spacing
    },
    draggable: false, // Synthetic nodes should not be draggable
    selectable: false, // Synthetic nodes should not be selectable
  }));
}

/**
 * Generate synthetic edges from terminal recon node to outcome nodes
 *
 * Creates edges connecting the terminal recon workflow node to each synthetic
 * outcome node. These edges are marked as synthetic and will not be saved to the API.
 *
 * @param reconNodeId - The ID of the terminal recon workflow node
 * @param syntheticOutcomeNodes - Array of synthetic outcome nodes
 * @param workflowOutcomes - Array of outcomes with precedence from the workflow
 * @returns Array of synthetic React Flow edges
 */
function generateSyntheticEdges(
  reconNodeId: string,
  syntheticOutcomeNodes: ReactFlowNode[],
  workflowOutcomes: WorkflowOutcome[],
): ReactFlowEdge[] {
  return syntheticOutcomeNodes.map((outcomeNode, index) => {
    const outcome = workflowOutcomes[index];
    const syntheticEdgeId = `synthetic-edge-${reconNodeId}-${index}`;

    return {
      id: syntheticEdgeId,
      source: reconNodeId,
      target: outcomeNode.id,
      type: 'straight',
      animated: false,
      sourceHandle: 'source',
      targetHandle: 'target',
      label: outcome.outcome,
      style: {
        ...DEFAULT_EDGE_STYLE,
      },
      data: {
        // Mark this edge as synthetic so it can be identified
        isSyntheticTerminalReconEdge: true,
        condition: outcome.outcome,
        precedence: outcome.precedence,
      },
    };
  });
}

/**
 * Generate all synthetic nodes and edges for non-legacy terminal recon workflows
 *
 * This is the main function that orchestrates the generation of synthetic
 * terminal recon outcome nodes and edges. It:
 * 1. Finds non-legacy terminal recon nodes
 * 2. Gets their workflow outcomes from cached data
 * 3. Generates synthetic outcome nodes
 * 4. Generates synthetic edges connecting recon node to outcome nodes
 *
 * @param document - The journey document
 * @param workflows - Workflow metadata with cached workflow data
 * @returns Object with synthetic nodes and edges arrays
 */
export function generateTerminalReconSynthetics(
  document: JourneyDocument,
  workflows: Record<string, JourneyWorkflowMetadata>,
): { nodes: ReactFlowNode[]; edges: ReactFlowEdge[] } {
  const syntheticNodes: ReactFlowNode[] = [];
  const syntheticEdges: ReactFlowEdge[] = [];

  // Find all reconciliation workflow nodes in the document
  const reconNodes = Object.values(document.nodes).filter(
    (node): node is ReconciliationWorkflowNode => node.type === 'reconciliation_workflow',
  );

  // Process each reconciliation workflow node
  reconNodes.forEach(reconNode => {
    // Skip if this is not a non-legacy terminal recon node
    if (!isNonLegacyTerminalReconNode(reconNode)) {
      return;
    }

    // Get workflow token and version from config
    const workflowToken = reconNode.config?.workflow_token;
    const workflowVersion = reconNode.config?.workflow_version;

    if (!workflowToken || !workflowVersion) {
      // eslint-disable-next-line no-console
      console.warn(
        `[TerminalReconSynthetics] Terminal recon node ${reconNode.id} missing workflow_token or workflow_version`,
      );
      return;
    }

    // Get workflow metadata and outcomes from Redux
    const workflowMeta = workflows[workflowToken];
    const workflowOutcomes = workflowMeta?.outcomes || [];

    if (workflowOutcomes.length === 0) {
      // Workflow data not yet loaded or has no outcomes
      // This is OK - it means preloadAllWorkflows hasn't run yet or is in progress
      // The graph will update once the workflow is loaded
      return;
    }

    // Generate synthetic outcome nodes for this terminal recon workflow
    const outcomeNodes = generateSyntheticOutcomeNodes(
      reconNode.id,
      workflowOutcomes,
    );

    // Generate synthetic edges from recon node to outcome nodes
    const edges = generateSyntheticEdges(
      reconNode.id,
      outcomeNodes,
      workflowOutcomes,
    );

    // Collect all synthetic elements
    syntheticNodes.push(...outcomeNodes);
    syntheticEdges.push(...edges);
  });

  return { nodes: syntheticNodes, edges: syntheticEdges };
}

